import documentTypeButtonGroupItems from "Areas/SharedFeatures/documentTypeButtonGroupItems";
import { shadingLightestColor } from "Common/componentUtils/modalUtils";
import { DOCUMENT_TYPE } from "Common/dictionaries/documentType";
import { formatSize, getExtensionFileFromName } from "Common/fileUtils/fileUtils";
import Button from "Components/button";
import ButtonGroup from "Components/buttonGroup";
import Close from "Components/close";
import { TextAreaWidget, TextWidget, hasEmptyRequiredFields } from "Components/optionForm";
import ScrolledDropdown, { TogglerWithFloatLabel } from "Components/scrolledDropdown";
import { themeVariables } from "Components/theme";
import WithModal from "Components/withModal";
import classNames from "classnames";
import includes from "lodash/includes";
import isUndefined from "lodash/isUndefined";
import join from "lodash/join";
import map from "lodash/map";
import toUpper from "lodash/toUpper";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { ALLOWED_EXTENSIONS, MAX_FILE_SIZE, requiredFields } from "../constants";
import mode from "../mode";
import locale from "./documentEditor.locale";
import Styled, { SIZES, getTextWidth } from "./documentEditor.styled";
import ImportForm from "./importForm";
const keySelector = (e) => e.Id;
const textSelector = (e) => e.Name;
const saveButtonText = {
    [mode.CREATE]: locale.CREATE,
    [mode.EDIT]: locale.SAVE,
    [mode.DUPLICATE]: locale.DUPLICATE
};
export default function DocumentEditor(props) {
    var _a, _b, _c, _d, _e;
    const { ref: observableRef } = useResizeDetector();
    const importFormRef = useRef(null);
    const [fileAttach, setFileAttach] = useState();
    const [showImport, setShowImport] = useState(false);
    const [sizeError, setSizeError] = useState(false);
    const [extensionError, setExtensionError] = useState(false);
    const [projects, setProjects] = useState(undefined);
    useEffect(() => {
        var _a, _b;
        props.mode === mode.DUPLICATE &&
            !projects &&
            ((_a = props.draft) === null || _a === void 0 ? void 0 : _a.Id) &&
            ((_b = props.draft) === null || _b === void 0 ? void 0 : _b.WorkSpaceId) &&
            props.onGetProjects &&
            props.onGetProjects(props.draft.Id, props.draft.WorkSpaceId, (data) => {
                setProjects(data);
            });
    }, [props.draft, props.mode]);
    useEffect(() => () => {
        if (props.documentId === undefined)
            setProjects(undefined);
    }, [props.documentId]);
    const reset = useCallback(() => {
        var _a;
        (_a = importFormRef.current) === null || _a === void 0 ? void 0 : _a.reset();
        setFileAttach(undefined);
        setExtensionError(false);
        setSizeError(false);
    }, []);
    const handleChangeDraft = useCallback((value, fieldName) => {
        fieldName &&
            props.onSetDraft &&
            props.onSetDraft({
                [fieldName]: value
            });
    }, [props.onSetDraft]);
    const isEmptyRequiredFields = hasEmptyRequiredFields(props.draft, requiredFields);
    const handleCancel = useCallback(() => {
        props.onSetDraft && props.onSetDraft({});
    }, [props.onSetDraft]);
    const handleFilesChanged = async ({ file }) => {
        if (props.onImportPreview) {
            props.onImportPreview(file, (data) => {
                var _a;
                const lines = data.Lines;
                (_a = importFormRef.current) === null || _a === void 0 ? void 0 : _a.setPreviewLines(lines);
                setFileAttach(file);
                setShowImport(true);
            });
        }
    };
    const handleValueChanged = ({ value }) => {
        const sizeInvalid = Boolean((value === null || value === void 0 ? void 0 : value.length) && value[0].size > MAX_FILE_SIZE);
        const extensionInvalid = Boolean((value === null || value === void 0 ? void 0 : value.length) && !includes(ALLOWED_EXTENSIONS, getExtensionFileFromName(value[0].name)));
        setSizeError(sizeInvalid);
        setExtensionError(extensionInvalid);
        if (sizeInvalid || extensionInvalid)
            return;
        (value === null || value === void 0 ? void 0 : value.length) && handleFilesChanged({ file: value[0] });
    };
    const handleCloseImport = useCallback(() => {
        setShowImport(false);
    }, []);
    const handleUploadCloseClick = useCallback(() => {
        reset();
    }, []);
    const importPanelFooter = useMemo(() => (React.createElement("div", { className: "d-flex justify-content-flex-end" },
        React.createElement("div", { className: "d-flex w-100" },
            React.createElement(Button, { icon: "arrowleft", text: locale.BACK, onClick: () => {
                    reset();
                    setShowImport(false);
                }, className: "btn-h-m p-8-24" })),
        React.createElement(Button, { text: locale.CANCEL, onClick: () => {
                reset();
                setShowImport(false);
            }, className: "btn-h-m p-8-24" }),
        React.createElement(Button, { text: locale.IMPORT_SAVE, className: "btn-primary ml-1 btn-h-m p-8-24", onClick: () => {
                setShowImport(false);
            } }))), []);
    const handleSave = useCallback(() => {
        props.mode &&
            props.draft &&
            props.onSaveDocument &&
            !isUndefined(props.documentId) &&
            props.onSaveDocument(props.draft, props.mode, fileAttach, fileAttach && importFormRef.current ? importFormRef.current.getImportParams() : undefined, reset);
    }, [props.draft, props.mode, fileAttach]);
    const handleDocumentTypeChanged = useCallback((itemData) => {
        reset();
        handleChangeDraft(itemData.key, "Type");
    }, [handleChangeDraft]);
    const selectButtonText = fileAttach ? fileAttach.name : locale.IMPORT_FILE;
    const uploadFileCloseIcon = useMemo(() => (React.createElement(Close, { fontSize: SIZES.UPLOAD_CLOSE.SIZE_FONT, size: SIZES.UPLOAD_CLOSE.SIZE, className: "bg-gray-200 border-rounded-circle position-absolute", style: {
            top: "0.5em",
            left: `calc(${getTextWidth(selectButtonText, themeVariables["font-family-base"], themeVariables["font-size-base"])}px +
    3em
)`
        }, btnModeBorderColor: "gray-200", onClick: handleUploadCloseClick })), [handleUploadCloseClick, selectButtonText]);
    if (!props.mode)
        return null;
    const hasImportPanel = props.mode !== mode.DUPLICATE && ((_a = props.draft) === null || _a === void 0 ? void 0 : _a.Type) === DOCUMENT_TYPE.TABLE;
    return (React.createElement(Styled.Container, { hasImportPanel: hasImportPanel, className: props.className },
        props.mode !== mode.DUPLICATE ? (React.createElement(ButtonGroup, { items: documentTypeButtonGroupItems, selectedItemKey: (_b = props.draft) === null || _b === void 0 ? void 0 : _b.Type, onItemClick: handleDocumentTypeChanged, className: "mb-gap" })) : (React.createElement("div", { className: "mb-gap" },
            React.createElement(ScrolledDropdown, { keySelector: keySelector, textSelector: textSelector, selectedKey: (_c = props.draft) === null || _c === void 0 ? void 0 : _c.ProjectId, onSelectedChanged: (id) => handleChangeDraft(id, "ProjectId"), items: projects, className: "dropdown-toggle-flex-space-between d-grid", maxWidth: "100%", togglerClassName: "border-rounded-medium p-4-0", caption: "", togglerComponent: React.createElement(TogglerWithFloatLabel, null), label: locale.PROJECT }))),
        React.createElement(TextWidget, { value: ((_d = props.draft) === null || _d === void 0 ? void 0 : _d.Name) || "", fieldName: "Name", onChange: handleChangeDraft, label: locale.NAME, labelMode: "floating", height: 45 }),
        React.createElement("div", { className: "overflow-hidden mt-gap", ref: observableRef },
            React.createElement(TextAreaWidget, { value: ((_e = props.draft) === null || _e === void 0 ? void 0 : _e.Description) || "", minHeight: "calc(100% - 8px)", maxHeight: "calc(100% - 8px)", height: "calc(100% - 8px)", autoResizeEnabled: false, fieldName: "Description", onChange: handleChangeDraft, label: locale.DESCRIPTION, labelMode: "floating" })),
        hasImportPanel && (React.createElement("div", { className: "d-flex mt-gap" },
            React.createElement("div", { className: "position-relative" },
                React.createElement(Styled.UploadButton, { selectButtonText: selectButtonText, uploadMode: "instantly", onValueChanged: handleValueChanged, size: fileAttach ? formatSize(fileAttach.size) : null, className: classNames(fileAttach && "chosen"), accept: join(map(ALLOWED_EXTENSIONS, (item) => `image/${item}`), ",") }),
                fileAttach && uploadFileCloseIcon),
            React.createElement(Styled.ErrorContainer, null, (sizeError || extensionError) && (React.createElement(React.Fragment, null,
                sizeError && React.createElement("span", null, locale.sizeMessage(formatSize(MAX_FILE_SIZE))),
                extensionError && (React.createElement("span", null, locale.fileExtensionMessage(join(map(ALLOWED_EXTENSIONS, toUpper), "/"))))))),
            React.createElement(WithModal, { show: showImport, onHide: handleCloseImport, contentClassName: "modal-padding", shadingColor: shadingLightestColor, className: "bg-blur", title: locale.IMPORT_TITLE, defaultWidth: SIZES.IMPORT_MODAL_WIDTH, defaultHeight: SIZES.IMPORT_MODAL_HEIGHT, isLoading: false, footer: importPanelFooter },
                React.createElement(ImportForm, { importFormRef: importFormRef })))),
        React.createElement("div", { className: "d-flex justify-content-flex-end with-gap-8 mt-24" },
            React.createElement(Button, { className: "btn-h-m p-8-24", text: locale.CANCEL, onClick: handleCancel, disabled: !props.hasChanges }),
            React.createElement(Button, { text: saveButtonText[props.mode], className: "btn-primary btn-h-m p-8-24", disabled: isEmptyRequiredFields || (!props.hasChanges && props.mode !== mode.DUPLICATE), onClick: handleSave }))));
}
